import { computed, inject, Injectable, signal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { IdTokenClaims, InteractionStatus } from '@azure/msal-browser';
import { User } from '@core/models/user.model';
import { IframeService } from '@core/services/iframe.service';
import { filter, firstValueFrom } from 'rxjs';

type SwissReIdTokenClaims = IdTokenClaims & { swissreuid?: string };

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _msalService = inject(MsalService);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _iframeService = inject(IframeService);

  user = signal<User | undefined>(undefined);
  isRiskManager = computed(() => this.user()?.isRiskManager || false);
  isInsightsAuthEligible = computed(() => this.user()?.userRoles?.includes('ESG_INSIGHTS_GENERATION') ?? false);
  hasInsightsAccess = signal(false);

  async login(allowInteractiveLogin = true): Promise<void> {
    await firstValueFrom(this._msalService.handleRedirectObservable());
    await firstValueFrom(
      this._msalBroadcastService.inProgress$.pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
    );

    if (!this.isActiveAccount() && allowInteractiveLogin) {
      if (this._iframeService.isEmbedded) {
        await firstValueFrom(this._msalService.loginPopup());
      } else {
        await firstValueFrom(this._msalService.loginRedirect());
      }
    }

    if (!this.isActiveAccount()) {
      return;
    }

    this.checkAndSetActiveAccount();
    this.loadUserInfo();
  }

  isActiveAccount(): boolean {
    return this._msalService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    const activeAccount = this._msalService.instance.getActiveAccount();

    if (!activeAccount && this.isActiveAccount()) {
      const accounts = this._msalService.instance.getAllAccounts();
      this._msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private loadUserInfo() {
    const userAccount = this._msalService.instance.getActiveAccount();

    if (!userAccount) {
      throw new Error('No active account found!');
    }

    const claims = userAccount.idTokenClaims as SwissReIdTokenClaims | undefined;
    const roles = claims?.roles;
    const loggedUser: User = {
      userId: claims?.swissreuid,
      userName: claims?.name ?? userAccount.name,
      userEmailAddress: claims?.preferred_username ?? userAccount.username,
      userRoles: roles,
      isRiskManager: roles?.includes('Admin'),
    };
    this.user.set(loggedUser);
  }
}
